import { useState, useEffect, useContext } from 'react';

import { GenericDropdown, UserContextMenu } from 'common-ui';
import { useNotifications } from 'ui-kit/hooks/useNotifications';
import { AuthContext } from 'context/AuthContext';
import { getUserAttributes } from 'utils/userAttributes';
import { UserModel } from 'features/pages/account/User';
import { Route, Routes } from 'react-router-dom';
import { DraggableHelpButton, PngIcon } from 'ui-kit';
import NotificationsDropdown from 'ui-kit/Notifications/NotificationsDropdown';
import { JobsDropdown } from 'features/jobs/JobsDropdown';

import { useLazyQuery } from '@apollo/client';

import { MAIN_HEADER_CONFIG } from './headers.config';
import ReferralLink from './ReferralLink';
import { SubHeader } from './SubHeader';
import {
  DataContext,
  DataContextActionType,
  initGlobalTableOrderConfig,
} from '../../../context/DataContext';
import { checkColumnCfgPayload } from '../../../hooks/useColumnOrder';
import { GetUserSetting } from '../../../query/__generated__/GetUserSetting';
import { GET_USER_SETTING } from '../../../query/userData';
import HeaderMenuItem from '../HeaderMenuItem/HeaderMenuItem';

const Header = () => {
  const { state: authState } = useContext(AuthContext);
  const { dispatch } = useContext(DataContext);
  const [userInfo, setUserInfo] = useState<UserModel>();
  const [blinkBell, setBlinkBell] = useState(false);

  const [getUserSetting] = useLazyQuery<GetUserSetting>(GET_USER_SETTING, {
    fetchPolicy: 'network-only',
  });

  const getUserTableSettings = async () => {
    try {
      const { data } = await getUserSetting({
        variables: { name: 'globalTableOrderConfig' },
      });

      if (data) {
        dispatch({
          type: DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG,
          payload: checkColumnCfgPayload(JSON.parse(data?.user.setting)),
        });
      }
    } catch {}
  };

  useEffect(() => {
    let isMounted = true;
    if (authState.user) {
      getUserTableSettings();
      getUserAttributes()
        .then((attributes) => {
          if (isMounted) {
            setUserInfo({
              givenName: attributes.given_name || '',
              familyName: attributes.family_name || '',
              email: attributes.email || '',
              phone: attributes.phone_number || '',
              title: '',
              company: authState.company?.name || '',
              address: {
                address1: '',
                suite: '',
                city: '',
                state: '',
                zipcode: '',
              },
              roles: '',
            });
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to get user attributes from token:', err);
          }
        });
    }
    return () => {
      isMounted = false;
    };
  }, [authState.user, authState.company]);

  const { notifications, loading } = useNotifications();
  const hasUnreadNotifications =
    notifications.filter((item) => Number(item.notificationMetadata.readAt) <= 0).length > 0;

  useEffect(() => {
    if (hasUnreadNotifications) {
      setBlinkBell(true);
      const timer = setTimeout(() => setBlinkBell(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [hasUnreadNotifications]);

  return (
    <header className="border-b border-slate-500 bg-slate-950 light:border-slate-100 light:bg-white">
      <nav className="grid grid-cols-[1fr_3fr_2fr] items-center pt-4 text-white grid-areas-header light:text-gray-600">
        <div className="ml-5 self-center justify-self-start [grid-area:header-logo]">
          <PngIcon name="logo" />
        </div>
        <div className="flex h-full items-end [grid-area:header-main]">
          <ul className="m-0 flex p-0">
            {MAIN_HEADER_CONFIG.map((headerMenuItem) => {
              return <HeaderMenuItem key={headerMenuItem.routeKey} tabDetails={headerMenuItem} />;
            })}
          </ul>
        </div>
        <div className="mr-5 flex items-center justify-end gap-5 [grid-area:header-actions]">
          <JobsDropdown />
          <div className="relative">
            <GenericDropdown iconType="svg" iconName="bell" position="right">
              <NotificationsDropdown
                notifications={notifications}
                loading={loading}
                className="max-h-[500px] w-[400px] bg-background-surface"
              />
            </GenericDropdown>
            {hasUnreadNotifications && (
              <span
                className={`absolute right-[10px] top-[10px] block h-2 w-2 rounded-full bg-blue-500 transition-opacity duration-300 ${blinkBell ? 'animate-ping' : ''}`}
              ></span>
            )}
          </div>
          <div>
            <GenericDropdown
              label={userInfo?.givenName ?? 'User'}
              iconType="avatar"
              position="right"
            >
              <UserContextMenu userInfo={userInfo} />
            </GenericDropdown>
          </div>
        </div>
        <Routes>
          <Route path=":mainSegment/*" element={<SubHeader />} />
        </Routes>
        <div className="absolute right-5 top-[71px]">
          <ReferralLink />
        </div>
        <DraggableHelpButton />
      </nav>
    </header>
  );
};

export default Header;
